import React from "react";
import { useNotification } from "./useNotification";
import { NotificationItem } from "./NotificationItem";
import styles from "./Notification.module.scss";
import { useSelector } from "react-redux";
import {
  selectNotificationsState,
  selectNotifications,
} from "Redux/Notifications/slice";
import {
  NotificationIcon as NotificationsNoneOutlined,
  CheckMarkIcon as DoneAllRounded,
} from "Assets";
import dayjs from "dayjs";
import Button from "Components/UI/Button/Button";
import { useAppDispatch } from "Redux/store";
import {
  getNotifications,
  getNotificationsMore,
} from "Redux/Notifications/reducer";
import { NOTIFICATIONS_LIMIT } from "Shared/Constants/app";
import SkeletonLoader from "Components/SkeletonLoader/SkeletonLoader";

type Props = {
  onClose: () => void;
  unreadOnly?: boolean;
};

export default function NotificationsList({
  onClose,
  unreadOnly = false,
}: Props) {
  const dispatch = useAppDispatch();
  const { notificationsList } = useNotification();
  const notifications = useSelector(selectNotifications);
  const {
    loading: isLoading,
    error,
    failedPage,
    actionLoading,
    hasLoaded,
  } = useSelector(selectNotificationsState);

  const handleLoadMore = () => {
    if (isLoading || actionLoading) return;
    const nextOffset = notifications.next
      ? Number(
          new URL(notifications.next, window.location.origin).searchParams.get(
            "offset",
          ),
        )
      : notifications.results.length;
    dispatch(
      getNotificationsMore({
        ...(unreadOnly ? { is_read: false } : {}),
        limit: NOTIFICATIONS_LIMIT,
        offset:
          Number.isFinite(nextOffset) && nextOffset > 0
            ? nextOffset
            : notifications.results.length,
      }),
    );
  };

  const visibleCount = notifications.results.filter(
    (item) => !unreadOnly || !item.is_read,
  ).length;

  if (
    (hasLoaded || !isLoading) &&
    !error &&
    !visibleCount &&
    !notifications.next
  ) {
    return (
      <div role="status" className={styles.empty}>
        {unreadOnly ? (
          <DoneAllRounded aria-hidden="true" />
        ) : (
          <NotificationsNoneOutlined aria-hidden="true" />
        )}
        <strong>
          {unreadOnly
            ? "You’re all caught up"
            : "There are no notifications yet"}
        </strong>
        <p>
          {unreadOnly
            ? "New unread reminders and updates will appear here."
            : "Your fleet’s reminders and updates will appear here."}
        </p>
      </div>
    );
  }

  if (!hasLoaded && !notifications.results.length && isLoading) {
    return (
      <SkeletonLoader
        variant="list"
        height="60px"
        amountOfBlocks={4}
        spacing={4}
      />
    );
  }

  return (
    <div aria-busy={isLoading}>
      {error && (
        <div role="alert" className={styles.error}>
          <p>
            {failedPage
              ? "Could not load more notifications."
              : "Could not refresh notifications."}{" "}
            Please try again.
          </p>
          <Button
            onClick={() =>
              failedPage
                ? handleLoadMore()
                : dispatch(
                    getNotifications({
                      offset: 0,
                      limit: NOTIFICATIONS_LIMIT,
                      forceRefresh: true,
                      ...(unreadOnly ? { is_read: false } : {}),
                    }),
                  )
            }
            disabled={isLoading || actionLoading}
          >
            Try again
          </Button>
        </div>
      )}
      <div>
        {notificationsList.map((group) => {
          const items = group.list
            .flatMap((category) => category.list)
            .filter((item) => !unreadOnly || !item.is_read)
            .sort(
              (a, b) => dayjs(b.created).valueOf() - dayjs(a.created).valueOf(),
            );
          return items.length ? (
            <section key={group.day.number} aria-label={group.day.title}>
              <h3 className={styles.dateTitle}>
                {group.day.title}
                <span>{items.length}</span>
              </h3>
              <ul className={styles.items}>
                {items.map((item) => (
                  <li key={item.id}>
                    <NotificationItem
                      onClose={onClose}
                      disabled={actionLoading}
                      notification={item}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ) : null;
        })}
      </div>
      {isLoading ? (
        <p role="status" className={styles.refreshStatus}>
          Updating notifications…
        </p>
      ) : (
        !!notifications.next &&
        !error && (
          <div className={styles.loadMore}>
            <Button fullWidth onClick={handleLoadMore} disabled={actionLoading}>
              Load more
            </Button>
          </div>
        )
      )}
    </div>
  );
}
